import React, { useEffect, useState } from 'react';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import {
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
} from '@/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Pencil, Trash2, ClipboardList, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ItemRaw {
  id: string;
  name: string;
  sku: string;
  buying_price: number | string;
  selling_price: number | string;
  quantity: number;
  vat_enabled?: number;
  barcode?: string;
  imeis?: string;
}
interface Item extends Omit<ItemRaw, 'buying_price' | 'selling_price'> {
  buying_price: number;
  selling_price: number;
  quantity: number;
    barcode?: string;
    imeis?: string;
    vat_enabled?: number;
}

export default function ItemsPage() {
  useEffect(() => {
    document.title = 'Items - Vixalor';
  }, []);
  const { toast } = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    buying_price: '',
    selling_price: '',
    barcode: '',
    imeis: '',
    hasImei: false,
    quantity: '',
    vat_enabled: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const currencySymbol = useCurrencySymbol();

  useEffect(() => {
    async function loadData() {
      try {
        const itemsRes = await fetchItems();
        const rawItems = (itemsRes.data as ItemRaw[]).map(i => ({
          ...i,
          buying_price: Number(i.buying_price),
          selling_price: Number(i.selling_price),
          quantity: Number(i.quantity),
          vat_enabled: Number(i.vat_enabled ?? 0),
          barcode: i.barcode || '',
          imeis: i.imeis || '',
        }));
        setItems(rawItems);
      } catch (err: any) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    }
    loadData();
  }, [toast]);

  const filtered = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.barcode && item.barcode.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const openDialog = (item?: Item) => {
    if (item) {
      setEditingItem(item);
      const parsedImeis = item.imeis ? JSON.parse(item.imeis).join(', ') : '';
      setFormData({
        name: item.name,
        sku: item.sku,
        buying_price: item.buying_price.toString(),
        selling_price: item.selling_price.toString(),
        barcode: item.barcode || '',
        imeis: parsedImeis,
        hasImei: !!item.imeis,
        quantity: item.quantity.toString(),
        vat_enabled: Boolean(item.vat_enabled),
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        sku: '',
        buying_price: '',
        selling_price: '',
        barcode: '',
        imeis: '',
        hasImei: false,
        quantity: '',
        vat_enabled: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, sku, buying_price, selling_price, barcode, imeis, hasImei, quantity, vat_enabled } = formData;
    if (!name || !sku || !buying_price || !selling_price) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    try {
      const imeisArray = hasImei && imeis ? imeis.split(',').map(s => s.trim()).filter(s => s) : null;
      const effectiveQuantity = hasImei ? 1 : (quantity || 0);
      const payload = {
        name,
        sku,
        buying_price: parseFloat(buying_price),
        selling_price: parseFloat(selling_price),
        barcode: barcode || null,
        imeis: imeisArray ? JSON.stringify(imeisArray) : null,
        quantity: parseInt(effectiveQuantity.toString(), 10),
        vat_enabled: vat_enabled ? 1 : 0,
      };

      if (editingItem) {
        await updateItem(editingItem.id, payload);
        toast({ title: 'Updated', description: 'Item updated successfully' });
      } else {
        await createItem(payload);
        toast({ title: 'Created', description: 'Item created successfully' });
      }

      const itemsRes = await fetchItems();
      const rawReloaded = (itemsRes.data as ItemRaw[]).map(i => ({
        ...i,
        buying_price: Number(i.buying_price),
        selling_price: Number(i.selling_price),
        quantity: Number(i.quantity),
        vat_enabled: Number(i.vat_enabled ?? 0),
        barcode: i.barcode || '',
        imeis: i.imeis || '',
      }));
      setItems(rawReloaded);
      setDialogOpen(false);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteItem(id);
      toast({ title: 'Deleted', description: 'Item deleted successfully' });
      const itemsRes = await fetchItems();
      const rawReloaded = (itemsRes.data as ItemRaw[]).map(i => ({
        ...i,
        buying_price: Number(i.buying_price),
        selling_price: Number(i.selling_price),
        quantity: Number(i.quantity),
        vat_enabled: Number(i.vat_enabled ?? 0),
        barcode: i.barcode || '',
        imeis: i.imeis || '',
      }));
      setItems(rawReloaded);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Items</h1>
        <p className="text-muted-foreground mt-1">Manage your inventory items.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              <CardTitle>Item Management</CardTitle>
            </div>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" /> Add New Item
            </Button>
          </div>
          <CardDescription>
            Add, edit, and manage your items. Quantities reflect current inventory.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items by name, SKU, or barcode..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="max-w-xs"
              />
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Barcode</TableHead>
                    <TableHead>IMEIs</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>VAT</TableHead>
                    <TableHead>Buying Price</TableHead>
                    <TableHead>Selling Price</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.barcode || '—'}</TableCell>
                      <TableCell>{item.imeis ? JSON.parse(item.imeis).join(', ') : '—'}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.vat_enabled ? 'Enabled' : 'Disabled'}</TableCell>
                      <TableCell>{currencySymbol}{item.buying_price.toFixed(2)}</TableCell>
                      <TableCell>{currencySymbol}{item.selling_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openDialog(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-w-full max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Edit an existing item.' : 'Add a new item.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                placeholder="Enter item name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">SKU</label>
              <Input
                placeholder="Enter SKU"
                value={formData.sku}
                onChange={e => setFormData({ ...formData, sku: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Barcode</label>
              <Input
                placeholder="Enter barcode (optional)"
                value={formData.barcode}
                onChange={e => setFormData({ ...formData, barcode: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="has-imei"
                checked={formData.hasImei}
                onCheckedChange={checked => setFormData({
                  ...formData,
                  hasImei: !!checked,
                  imeis: checked ? formData.imeis : '',
                  quantity: checked ? '1' : formData.quantity,
                })}
              />
              <label htmlFor="has-imei" className="text-sm font-medium">Enable IMEI</label>
            </div>
            {formData.hasImei && (
              <div className="space-y-2">
                <label className="text-sm font-medium">IMEIs (comma-separated)</label>
                <Input
                  placeholder="Enter IMEIs, e.g., 123456789012345, 987654321098765"
                  value={formData.imeis}
                  onChange={e => setFormData({ ...formData, imeis: e.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Quantity</label>
              <Input
                type="number"
                placeholder="Enter quantity"
                value={formData.hasImei ? '1' : formData.quantity}
                onChange={e => !formData.hasImei && setFormData({ ...formData, quantity: e.target.value })}
                disabled={formData.hasImei}
                required
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vat-enabled"
                checked={formData.vat_enabled}
                onCheckedChange={checked => setFormData({ ...formData, vat_enabled: !!checked })}
              />
              <label htmlFor="vat-enabled" className="text-sm font-medium">Enable VAT for this item</label>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Buying Price</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter buying price"
                value={formData.buying_price}
                onChange={e => setFormData({ ...formData, buying_price: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Selling Price</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter selling price"
                value={formData.selling_price}
                onChange={e => setFormData({ ...formData, selling_price: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              {editingItem ? 'Update Item' : 'Save Item'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
